import { Item, ItemGiftwareTableware, ItemType } from '../type'
import { translate } from '../../../core'
import { Button } from 'antd'
import { PrinterOutlined } from '@ant-design/icons'
import React, { useState } from 'react'
import { Nullable, QueryPageableRequest, QueryPageableResponse } from '../../../core/type'
import { PrintJewelryVariables, PrintTablewareVariables, usePrinter } from '../../../core/component/Printer/printer.hook'
import { usePrintQueue } from '../../../core/component/Printer/usePrintQueue'
import { buildJewelryPrintJobs } from '../func/jewelery-print-payloads'
import { getCodedPrice } from '../func/price-code'
import { useLazyQuery } from '@apollo/client'
import { ItemFindRequest } from '../type/item-query'
import { GetItemsForPage } from '../graphql/item.graphql'
import mapStoneShape from '../func/stone-shap-mapping'
import { compactNumberText } from '../../../core/until'

export type ItemPagePrintButtonProps = {
  item: Nullable<Item>
}

export const ItemPagePrintButton = ({ item }: ItemPagePrintButtonProps) => {
  const { print } = usePrinter()
  const { run: runPrintQueue, modal: printQueueModal, busy } = usePrintQueue()
  const [printing, setPrinting] = useState(false)
  const [find] = useLazyQuery<QueryPageableResponse<Item, 'itemsFind'>,
    QueryPageableRequest<ItemFindRequest>>(GetItemsForPage)

  const fetch = async () => {
    if (!item)
      return

    const { data } = await find({
      variables: {
        request: {
          id: item.id,
          take: 1,
          skip: 0,
        }
      }
    })

    const [entity] = data?.itemsFind.data ?? []
    return entity
  }

  const handleJeweleryPrint = (entity: Item) => {
    if (!entity)
      return

    return runPrintQueue(buildJewelryPrintJobs(entity))
  }

  const handleWatchPrint = (entity: Item) => {
    if (!entity)
      return

    const caseSize = entity.watch?.caseSize

    return print<PrintJewelryVariables>({
      template_name: 'jewelry',
      variables: {
        firstText: entity.style,
        secondText: caseSize && Number(caseSize) > 0 ? `${caseSize} mm` : '',
        thirdText: '',
        fourthText: entity.vendor?.contactId,
        fifthText: entity.sku,
        sixthText: `R${entity.listPrice}`,
        barcode: entity.sku,
      },
    })
  }

  const handleTablewarePrint = (entity: Item) => {
    if (!entity)
      return

    const innerValue = (
      entity.type === ItemType.Tableware
        ? entity.tableware
        : entity.giftware
    ) ?? {} as ItemGiftwareTableware

    return print<PrintTablewareVariables>({
      template_name: 'tableware',
      variables: {
        firstText: innerValue.patternNumber,
        secondText: entity.style,
        thirdText: getCodedPrice(entity.listPrice),
        fourthText: entity.description,
        barcode: entity.sku,
      },
    })
  }

  const handleStonePrint = (entity: Item) => {
    if (!entity)
      return

    const { stone } = entity
    const name = stone?.name ?? ''

    const DIAMOND = 'DIAMOND'
    const LAB_DIAMOND = 'LAB DIAMOND'

    const firstText = ([
      mapStoneShape(stone?.shape),

      ...([DIAMOND, LAB_DIAMOND].includes(name)
        ? [
          stone?.color ?? '',
          stone?.clarity ?? '',
        ]
        : [
          stone?.name ?? ''
        ])
    ]).join(' / ')

    const secondText = (
      [DIAMOND, LAB_DIAMOND].includes(name)
        ? [
          name === LAB_DIAMOND
            ? 'Lab Grown'
            : stone?.lab,
          name === LAB_DIAMOND
            ? null
            : stone?.certificateNumber,
        ]
          .filter(Boolean)
        : []
    ).join(' / ')


    return print<PrintJewelryVariables>({
      template_name: 'jewelry',
      variables: {
        firstText,
        secondText,
        thirdText: `R ${compactNumberText(stone?.rap)}`,
        fourthText: `TW ${(stone?.totalWeight ?? 0).toFixed(2)}`,
        fifthText: '',
        sixthText: getCodedPrice(stone?.costCT ?? 0, true),
        barcode: entity.sku,
      }
    })
  }

  return (
    <>
      <Button
        onClick={async () => {
          // Block re-entry for the whole operation (fetch + print) so a
          // double-click cannot send duplicate tags to the printer.
          if (printing)
            return

          setPrinting(true)
          try {
            const entity = await fetch()

            if (!entity)
              return

            if (entity.type === ItemType.Jewelery)
              return await handleJeweleryPrint(entity)

            if (entity.type === ItemType.Watch)
              return await handleWatchPrint(entity)

            if ([ItemType.Tableware, ItemType.Giftware].includes(entity.type))
              return await handleTablewarePrint(entity)

            if (entity.type === ItemType.Stone)
              return await handleStonePrint(entity)
          } finally {
            setPrinting(false)
          }
        }}
        disabled={!item || printing || busy}
        loading={printing || busy}
        icon={<PrinterOutlined />}
      >
        {translate('print', 'tag')}
      </Button>
      {printQueueModal}
    </>
  )
}